import { useCallback, useMemo, useState, useEffect } from "react";
import { useFiltersContext } from "../../../../pages/Workspace/UserFilters/FiltersContext";
import {
  InclusionFilter,
  InclusionMethod,
} from "../../../../legacy/types/companyFilter";
import { FILTER_MODE } from "../../Condition/constants";
import { ProductFilterValue } from "../../ProductFilter/types";
import { FilterMode } from "../../Condition/types";

export interface ProductSection {
  id: string;
  condition: FilterMode;
  index: number;
  productIds: number[];
  faiIds?: string[];
  departmentIds?: string[];
  roleIds?: string[];
  installAgeMonths?: { min?: number | null; max?: number | null };
  intensity?: { min?: number | null; max?: number | null };
}

export const useProductSections = () => {
  const { draftFilters, setDraftFilters } = useFiltersContext();

  // Initialize UI sections state
  const [uiSections, setUiSections] = useState<ProductSection[]>(() => {
    // Start with one empty section by default
    return [
      {
        id: "product-inclusion-0",
        condition: FILTER_MODE.ANY,
        index: 0,
        productIds: [],
      },
    ];
  });

  // Track if this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Track previous products to detect external clears
  const [prevProductsLength, setPrevProductsLength] = useState(0);

  // Sync UI sections with draftFilters on initial load and when externally cleared
  useEffect(() => {
    const products = draftFilters?.products || [];
    const inclusionProducts = products.filter(
      (p) => p.inclusionMethod !== InclusionMethod.NONE_PRESENT
    );

    // Case 1: Initial load with existing products - load them into UI
    if (isInitialLoad && inclusionProducts.length > 0) {
      const sections: ProductSection[] = inclusionProducts.map(
        (product, index) => {
          const faiIds = [
            ...(product.departmentIds || []),
            ...(product.roleIds || []),
          ];

          return {
            id: `product-inclusion-${index}`,
            condition: product.inclusionMethod as FilterMode,
            index,
            productIds: product.ids || [],
            faiIds,
            departmentIds: product.departmentIds || [],
            roleIds: product.roleIds || [],
            installAgeMonths: product.installAgeMonths ?? undefined,
            intensity: product.intensity ?? undefined,
          };
        }
      );

      setUiSections(sections);
      setIsInitialLoad(false);
      setPrevProductsLength(products.length);
      return;
    }

    // Case 2: Initial load with no products - just mark as loaded
    if (isInitialLoad && products.length === 0) {
      setIsInitialLoad(false);
      setPrevProductsLength(0);
      return;
    }

    // Case 3: External clear detected - reset to default state
    const wasExternallyCleared =
      !isInitialLoad && prevProductsLength > 0 && products.length === 0;

    if (wasExternallyCleared) {
      setUiSections([
        {
          id: "product-inclusion-0",
          condition: FILTER_MODE.ANY,
          index: 0,
          productIds: [],
        },
      ]);
      setPrevProductsLength(0);
      return;
    }

    // Case 4: Regular update - just track the length for future comparisons
    if (!isInitialLoad) {
      setPrevProductsLength(products.length);
    }
  }, [draftFilters?.products, isInitialLoad, prevProductsLength]);

  // Get exclusion products (NONE_PRESENT)
  const exclusionProducts = useMemo(() => {
    const products = draftFilters?.products || [];
    const exclusionFilter = products.find(
      (p) => p.inclusionMethod === InclusionMethod.NONE_PRESENT
    );

    return exclusionFilter?.ids || [];
  }, [draftFilters?.products]);

  // Helper to safely update a section at a specific index
  const updateSectionAtIndex = (
    sections: ProductSection[],
    sectionIndex: number,
    updates: Partial<ProductSection>
  ): ProductSection[] => {
    const newSections = [...sections];
    // Only update if the section index is valid to prevent out-of-bounds errors
    if (sectionIndex < newSections.length) {
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        ...updates,
      };
    }
    return newSections;
  };

  // Helper to sync UI sections to draftFilters
  const syncSectionsToDraftFilters = useCallback(
    (sections: ProductSection[]) => {
      setDraftFilters((prev) => {
        // Get existing exclusion products
        const currentProducts = prev?.products || [];
        const exclusionProducts = currentProducts.filter(
          (p) => p.inclusionMethod === InclusionMethod.NONE_PRESENT
        );

        // Convert UI sections to inclusion filters
        const inclusionProducts: InclusionFilter[] = sections
          .filter((section) => section.productIds.length > 0) // Only include sections with products
          .map((section) => {
            const filter: InclusionFilter = {
              ids: section.productIds,
              inclusionMethod: section.condition,
            };

            // Add FAI fields if present
            if (section.departmentIds && section.departmentIds.length > 0) {
              filter.departmentIds = section.departmentIds;
            }
            if (section.roleIds && section.roleIds.length > 0) {
              filter.roleIds = section.roleIds;
            }

            // Add install age if present
            if (
              section.installAgeMonths &&
              (section.installAgeMonths.min !== undefined ||
                section.installAgeMonths.max !== undefined)
            ) {
              filter.installAgeMonths = section.installAgeMonths;
            }

            // Add intensity if present
            if (
              section.intensity &&
              (section.intensity.min !== undefined ||
                section.intensity.max !== undefined)
            ) {
              filter.intensity = section.intensity;
            }

            return filter;
          });

        // Combine inclusion and exclusion products
        const finalProducts = [...inclusionProducts, ...exclusionProducts];

        return {
          ...prev,
          products: finalProducts.length > 0 ? finalProducts : undefined,
        };
      });
    },
    [setDraftFilters]
  );

  // Handle product selection for inclusion sections
  const handleInclusionProductSelect = useCallback(
    (sectionIndex: number, value: ProductFilterValue) => {
      const newSections = updateSectionAtIndex(uiSections, sectionIndex, {
        productIds: value.ids,
      });

      setUiSections(newSections);
      // Sync to draft filters after state update
      syncSectionsToDraftFilters(newSections);
    },
    [uiSections, syncSectionsToDraftFilters]
  );

  // Handle condition change for inclusion sections
  const handleConditionChange = useCallback(
    (sectionIndex: number, condition: FilterMode) => {
      const newSections = updateSectionAtIndex(uiSections, sectionIndex, {
        condition,
      });

      setUiSections(newSections);
      // Sync to draft filters after state update
      syncSectionsToDraftFilters(newSections);
    },
    [uiSections, syncSectionsToDraftFilters]
  );

  // Handle exclusion product selection
  const handleExclusionProductSelect = useCallback(
    (value: ProductFilterValue) => {
      setDraftFilters((prev) => {
        const currentProducts = [...(prev?.products || [])];

        // Remove existing exclusion filter
        const filteredProducts = currentProducts.filter(
          (p) => p.inclusionMethod !== InclusionMethod.NONE_PRESENT
        );

        // Add new exclusion filter if there are products selected
        if (value.ids.length > 0) {
          filteredProducts.push({
            ids: value.ids,
            inclusionMethod: InclusionMethod.NONE_PRESENT,
          });
        }

        return {
          ...prev,
          products: filteredProducts.length > 0 ? filteredProducts : undefined,
        };
      });
    },
    [setDraftFilters]
  );

  // Add new inclusion section
  const handleAddSection = useCallback(() => {
    setUiSections((prevSections) => {
      const newIndex = prevSections.length;
      return [
        ...prevSections,
        {
          id: `product-inclusion-${newIndex}`,
          condition: FILTER_MODE.ANY,
          index: newIndex,
          productIds: [],
        },
      ];
    });
  }, []);

  // Remove inclusion section
  const handleRemoveSection = useCallback(
    (sectionIndex: number) => {
      const newSections = uiSections.filter(
        (_, index) => index !== sectionIndex
      );

      // Re-index sections
      const reindexedSections = newSections.map((section, index) => ({
        ...section,
        id: `product-inclusion-${index}`,
        index,
      }));

      setUiSections(reindexedSections);
      // Sync to draft filters after state update
      syncSectionsToDraftFilters(reindexedSections);
    },
    [uiSections, syncSectionsToDraftFilters]
  );

  // Clear all filters
  const handleClearFilter = useCallback(() => {
    // Reset UI sections to default
    setUiSections([
      {
        id: "product-inclusion-0",
        condition: FILTER_MODE.ANY,
        index: 0,
        productIds: [],
      },
    ]);

    // Clear draft filters
    setDraftFilters((prev) => ({
      ...prev,
      products: undefined,
    }));
  }, [setDraftFilters]);

  const handleFaiSelect = useCallback(
    (
      sectionIndex: number,
      selectedDepartmentIds: string[],
      selectedRoleIds: string[]
    ) => {
      const faiIds = [...selectedDepartmentIds, ...selectedRoleIds];
      const newSections = updateSectionAtIndex(uiSections, sectionIndex, {
        faiIds: faiIds.length > 0 ? faiIds : undefined,
        departmentIds:
          selectedDepartmentIds.length > 0 ? selectedDepartmentIds : undefined,
        roleIds: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
      });

      setUiSections(newSections);
      // Sync to draft filters after state update
      syncSectionsToDraftFilters(newSections);
    },
    [uiSections, syncSectionsToDraftFilters]
  );

  const handleInstallAgeChange = useCallback(
    (
      sectionIndex: number,
      installAge: { min?: number | null; max?: number | null }
    ) => {
      const newSections = updateSectionAtIndex(uiSections, sectionIndex, {
        installAgeMonths:
          installAge.min === undefined && installAge.max === undefined
            ? undefined
            : installAge,
      });

      setUiSections(newSections);
      // Sync to draft filters after state update
      syncSectionsToDraftFilters(newSections);
    },
    [uiSections, syncSectionsToDraftFilters]
  );

  const handleIntensityChange = useCallback(
    (
      sectionIndex: number,
      intensity: { min?: number | null; max?: number | null }
    ) => {
      const newSections = updateSectionAtIndex(uiSections, sectionIndex, {
        intensity:
          intensity.min === undefined && intensity.max === undefined
            ? undefined
            : intensity,
      });

      setUiSections(newSections);
      // Sync to draft filters after state update
      syncSectionsToDraftFilters(newSections);
    },
    [uiSections, syncSectionsToDraftFilters]
  );

  return {
    inclusionSections: uiSections,
    exclusionProducts,
    handleInclusionProductSelect,
    handleConditionChange,
    handleExclusionProductSelect,
    handleAddSection,
    handleRemoveSection,
    handleClearFilter,
    handleFaiSelect,
    handleInstallAgeChange,
    handleIntensityChange,
  };
};
